import React, { useState, useEffect } from 'react';

const DriverMapView = ({ isOnline, activeOrder, navigationActive, onToggleNavigation }) => {
  const [driverPos, setDriverPos] = useState({ x: 50, y: 50 });
  const [heading, setHeading] = useState(45);

  useEffect(() => {
    const interval = setInterval(() => {
      setDriverPos(prev => ({
        x: Math.max(10, Math.min(90, prev?.x + (Math.random() - 0.5) * 1.5)),
        y: Math.max(10, Math.min(90, prev?.y + (Math.random() - 0.5) * 1.5)),
      }));
      setHeading(prev => (prev + (Math.random() - 0.5) * 10 + 360) % 360);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const nearbyOrders = [
    { x: 30, y: 35, priority: 'high', type: 'ride', fare: 850 },
    { x: 65, y: 25, priority: 'normal', type: 'delivery', fare: 320 },
    { x: 75, y: 65, priority: 'normal', type: 'ride', fare: 540 },
    { x: 20, y: 70, priority: 'high', type: 'delivery', fare: 680 },
  ];

  const routePoints = activeOrder
    ? `${driverPos?.x * 3.6},${driverPos?.y * 2.4} ${driverPos?.x * 3.6 - 40},${driverPos?.y * 2.4 - 60} 180,80`
    : null;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ height: '220px' }}>
      {/* Map SVG */}
      <svg viewBox="0 0 360 240" className="w-full h-full" style={{ background: '#0d1f3c' }}>
        {/* Grid lines */}
        {[0, 60, 120, 180, 240, 300, 360]?.map(x => (
          <line key={`vl-${x}`} x1={x} y1="0" x2={x} y2="240" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}
        {[0, 60, 120, 180, 240]?.map(y => (
          <line key={`hl-${y}`} x1="0" y1={y} x2="360" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}

        {/* Roads */}
        <path d="M0,120 Q90,100 180,120 Q270,140 360,120" stroke="rgba(255,255,255,0.12)" strokeWidth="8" fill="none" strokeLinecap="round" />
        <path d="M0,120 Q90,100 180,120 Q270,140 360,120" stroke="rgba(255,255,255,0.06)" strokeWidth="12" fill="none" strokeLinecap="round" />
        <path d="M180,0 Q160,60 180,120 Q200,180 180,240" stroke="rgba(255,255,255,0.10)" strokeWidth="6" fill="none" strokeLinecap="round" />
        <path d="M60,0 Q80,80 60,160 Q40,200 60,240" stroke="rgba(255,255,255,0.07)" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M300,0 Q280,80 300,160 Q320,200 300,240" stroke="rgba(255,255,255,0.07)" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M0,60 Q120,50 240,60 Q300,65 360,60" stroke="rgba(255,255,255,0.06)" strokeWidth="3" fill="none" />
        <path d="M0,180 Q120,170 240,180 Q300,185 360,180" stroke="rgba(255,255,255,0.06)" strokeWidth="3" fill="none" />

        {/* Blocks */}
        {[[20,20,50,30],[100,20,60,30],[200,20,50,30],[280,20,60,30],[20,80,40,30],[100,80,50,30],[220,80,60,30],[300,80,40,30],[20,150,50,25],[110,150,60,25],[220,150,50,25],[300,150,40,25],[20,195,50,30],[110,195,60,30],[220,195,50,30],[300,195,40,30]]?.map(([x,y,w,h], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} rx="3" fill="rgba(255,255,255,0.04)" />
        ))}

        {/* Active route */}
        {routePoints && (
          <>
            <polyline points={routePoints} stroke="#ffd700" strokeWidth="3" fill="none" strokeDasharray="8,4" opacity="0.8" />
            <circle cx={180} cy={80} r="8" fill="#ffd700" opacity="0.9" />
            <circle cx={180} cy={80} r="14" fill="none" stroke="#ffd700" strokeWidth="2" opacity="0.4" />
          </>
        )}

        {/* Nearby order pins */}
        {nearbyOrders?.map((order, i) => {
          const px = order?.x * 3.6;
          const py = order?.y * 2.4;
          return (
            <g key={i}>
              <circle cx={px} cy={py} r="14" fill={order?.priority === 'high' ? 'rgba(255,215,0,0.15)' : 'rgba(96,165,250,0.15)'} />
              <circle cx={px} cy={py} r="9" fill={order?.priority === 'high' ? '#ffd700' : '#60a5fa'} />
              <text x={px} y={py + 4} textAnchor="middle" fontSize="8" fill={order?.priority === 'high' ? '#0a1628' : '#fff'} fontWeight="bold">
                {order?.type === 'ride' ? '🚗' : '📦'}
              </text>
            </g>
          );
        })}

        {/* Driver position */}
        <g transform={`translate(${driverPos?.x * 3.6}, ${driverPos?.y * 2.4})`}>
          {/* Accuracy ring */}
          <circle cx="0" cy="0" r="22" fill="rgba(255,215,0,0.08)" />
          <circle cx="0" cy="0" r="16" fill="rgba(255,215,0,0.12)" />
          {/* Direction indicator */}
          <g transform={`rotate(${heading})`}>
            <polygon points="0,-14 -5,-4 5,-4" fill="#ffd700" opacity="0.7" />
          </g>
          {/* Driver dot */}
          <circle cx="0" cy="0" r="8" fill="#ffd700" />
          <circle cx="0" cy="0" r="5" fill="#0a1628" />
          <circle cx="0" cy="0" r="2" fill="#ffd700" />
        </g>
      </svg>
      {/* Overlay controls */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
        {/* GPS status */}
        <div className="flex items-center gap-1.5 bg-[#0a1628]/80 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/10">
          <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-white/30'}`} />
          <span className="text-white/70 text-xs font-medium">GPS · High Accuracy</span>
        </div>

        {/* Navigation toggle */}
        <button
          onClick={onToggleNavigation}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 border transition-all ${
            navigationActive
              ? 'bg-[#ffd700] border-[#ffd700] text-[#0a1628]'
              : 'bg-[#0a1628]/80 backdrop-blur-sm border-white/10 text-white/70'
          }`}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
            <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
          </svg>
          <span className="text-xs font-semibold">{navigationActive ? 'Stop Nav' : 'Navigate'}</span>
        </button>
      </div>
      {/* Active order destination */}
      {activeOrder && (
        <div className="absolute bottom-3 left-3 right-3 bg-[#0a1628]/90 backdrop-blur-sm rounded-xl p-2.5 border border-[#ffd700]/20">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-[#ffd700]/20 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="#ffd700" className="w-3.5 h-3.5">
                <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/40 text-[10px]">Navigating to pickup</p>
              <p className="text-white text-xs font-semibold truncate">{activeOrder?.pickup}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[#ffd700] text-xs font-bold">6 min</p>
              <p className="text-white/40 text-[10px]">2.4 km</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverMapView;
